import React, { useState, useEffect } from 'react';
import { FileText, Upload, Search, Filter, Trash2, Archive, Grid, List, User } from 'lucide-react';
import Header from '../../components/navigation/Header';
import GlobalFilterBar from '../../components/navigation/GlobalFilterBar';
import DataRefreshIndicator from '../../components/navigation/DataRefreshIndicator';
import LegalDisclaimerBanner from '../../components/navigation/LegalDisclaimerBanner';
import ComplianceAlertBanner from '../../components/navigation/ComplianceAlertBanner';
import Button from '../../components/ui/Button';

import documentManagementService from '../../services/documentManagementService';
import employeeService from '../../services/employeeService';
import { useAuth } from '../../contexts/AuthContext';
import { useCompanySettings } from '../../contexts/CompanySettingsContext';
import DocumentUploadModal from './components/DocumentUploadModal';
import DocumentCard from './components/DocumentCard';
import DocumentPreviewModal from './components/DocumentPreviewModal';
import DocumentFilterPanel from './components/DocumentFilterPanel';
import DocumentStatisticsCard from './components/DocumentStatisticsCard';

const DocumentManagementCenter = () => {
  const { user } = useAuth();
  const { companySettings } = useCompanySettings();
  const companyName = companySettings?.companyName || 'HR Analytics';

  // State
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // UI State
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [selectedDocuments, setSelectedDocuments] = useState([]);

  // Filters
  const [filters, setFilters] = useState({
    category: null,
    startDate: null,
    endDate: null,
    department: null,
    employeeId: null,
    tags: [],
    includeArchived: false
  });

  // Categories
  const categories = [
    { id: 'all', label: 'All Documents', icon: 'FileText', count: 0 },
    { id: 'payslip', label: 'Payslips', icon: 'DollarSign', count: 0 },
    { id: 'employment_letter', label: 'Employment Letters', icon: 'Mail', count: 0 },
    { id: 'contract', label: 'Contracts', icon: 'FileCheck', count: 0 },
    { id: 'compliance', label: 'Compliance Records', icon: 'Shield', count: 0 },
    { id: 'personal', label: 'Personal Documents', icon: 'User', count: 0 }
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [documents, selectedCategory, searchTerm, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [docsData, employeesData, statsData] = await Promise.all([
        documentManagementService?.searchDocuments({ includeArchived: false }),
        employeeService?.getAllEmployees(),
        documentManagementService?.getDocumentStatistics()
      ]);

      setDocuments(docsData || []);
      setEmployees(employeesData || []);
      setStatistics(statsData);
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err?.message || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...documents];

    // Category filter
    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(doc => doc?.category === selectedCategory);
    }

    // Search filter
    if (searchTerm) {
      const term = searchTerm?.toLowerCase();
      filtered = filtered?.filter(doc =>
        doc?.documentName?.toLowerCase()?.includes(term) ||
        doc?.fileName?.toLowerCase()?.includes(term) ||
        doc?.employeeName?.toLowerCase()?.includes(term) ||
        doc?.description?.toLowerCase()?.includes(term)
      );
    }

    // Advanced filters
    if (filters?.department) {
      filtered = filtered?.filter(doc => doc?.department === filters?.department);
    }

    if (filters?.employeeId) {
      filtered = filtered?.filter(doc => doc?.employeeId === filters?.employeeId);
    }

    if (filters?.startDate) {
      filtered = filtered?.filter(doc => new Date(doc?.documentDate) >= new Date(filters?.startDate));
    }

    if (filters?.endDate) {
      filtered = filtered?.filter(doc => new Date(doc?.documentDate) <= new Date(filters?.endDate));
    }

    if (filters?.tags?.length > 0) {
      filtered = filtered?.filter(doc =>
        filters?.tags?.some(tag => doc?.tags?.includes(tag))
      );
    }

    setFilteredDocuments(filtered);
  };

  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    setSuccess('Document uploaded successfully');
    loadData();
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleDownload = async (document) => {
    try {
      await documentManagementService?.downloadDocument(document?.storagePath, document?.fileName);
      setSuccess('Document downloaded successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to download document');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handlePreview = (document) => {
    setSelectedDocument(document);
    setShowPreviewModal(true);
  };

  const handleArchive = async (documentId) => {
    try {
      await documentManagementService?.archiveDocument(documentId);
      setSuccess('Document archived successfully');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to archive document');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleDelete = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document? This action cannot be undone.')) {
      return;
    }

    try {
      await documentManagementService?.deleteDocument(documentId);
      setSuccess('Document deleted successfully');
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to delete document');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleBulkArchive = async () => {
    if (selectedDocuments?.length === 0) return;

    try {
      await documentManagementService?.bulkArchive(selectedDocuments);
      setSuccess(`${selectedDocuments?.length} documents archived successfully`);
      setSelectedDocuments([]);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to archive documents');
      setTimeout(() => setError(''), 3000);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedDocuments?.length === 0) return;

    if (!window.confirm(`Are you sure you want to delete ${selectedDocuments?.length} documents? This action cannot be undone.`)) {
      return;
    }

    try {
      await documentManagementService?.bulkDelete(selectedDocuments);
      setSuccess(`${selectedDocuments?.length} documents deleted successfully`);
      setSelectedDocuments([]);
      loadData();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err?.message || 'Failed to delete documents');
      setTimeout(() => setError(''), 3000);
    }
  };

  const toggleDocumentSelection = (documentId) => {
    setSelectedDocuments(prev =>
      prev?.includes(documentId)
        ? prev?.filter(id => id !== documentId)
        : [...prev, documentId]
    );
  };

  const getCategoryCount = (categoryId) => {
    if (categoryId === 'all') return documents?.length;
    return documents?.filter(doc => doc?.category === categoryId)?.length;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <GlobalFilterBar onFilterChange={() => {}} />
        <DataRefreshIndicator />
        <LegalDisclaimerBanner />
        <ComplianceAlertBanner />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading documents...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <GlobalFilterBar onFilterChange={() => {}} />
      <DataRefreshIndicator />
      <LegalDisclaimerBanner />
      <ComplianceAlertBanner />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Document Management Center</h1>
          <p className="text-gray-600">Centralized storage and retrieval for all employee documents</p>
        </div>

        {/* Success/Error Messages */}
        {success && (
          <div className="mb-6 bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Statistics Cards */}
        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <DocumentStatisticsCard
              title="Total Documents"
              value={statistics?.totalDocuments || 0}
              icon="FileText"
              color="blue"
            />
            <DocumentStatisticsCard
              title="Total Storage"
              value={`${((statistics?.totalSizeBytes || 0) / 1024 / 1024)?.toFixed(2)} MB`}
              icon="HardDrive"
              color="purple"
            />
            <DocumentStatisticsCard
              title="Categories"
              value={Object.keys(statistics?.categoryBreakdown || {})?.length}
              icon="FolderOpen"
              color="emerald"
            />
          </div>
        )}

        <div className="grid grid-cols-12 gap-6">
          {/* Category Navigation */}
          <div className="col-span-12 lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Categories</h2>
              <div className="space-y-2">
                {categories?.map(category => (
                  <button
                    key={category?.id}
                    onClick={() => setSelectedCategory(category?.id)}
                    className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-colors ${
                      selectedCategory === category?.id
                        ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' :'hover:bg-gray-50 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5" />
                      <span className="font-medium">{category?.label}</span>
                    </div>
                    <span className="text-sm font-semibold">
                      {getCategoryCount(category?.id)}
                    </span>
                  </button>
                ))}
              </div>

              <div className="mt-6 pt-6 border-t border-gray-200">
                <Button
                  onClick={() => setShowUploadModal(true)}
                  iconName="Upload"
                  fullWidth
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  Upload Document
                </Button>
              </div>
            </div>
          </div>

          {/* Main Document Workspace */}
          <div className="col-span-12 lg:col-span-9">
            {/* Toolbar */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
              <div className="flex flex-col md:flex-row gap-4 items-center justify-between">
                {/* Search */}
                <div className="flex-1 w-full md:w-auto">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                    <input
                      type="text"
                      placeholder="Search documents..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e?.target?.value)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    />
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    onClick={() => setShowFilterPanel(!showFilterPanel)}
                    iconName="Filter"
                    variant="outline"
                    size="sm"
                  >
                    Filters
                  </Button>

                  <div className="flex items-center gap-1 border border-gray-300 rounded-lg p-1">
                    <button
                      onClick={() => setViewMode('grid')}
                      className={`p-2 rounded ${
                        viewMode === 'grid' ?'bg-emerald-100 text-emerald-700' :'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <Grid className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded ${
                        viewMode === 'list' ?'bg-emerald-100 text-emerald-700' :'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <List className="w-4 h-4" />
                    </button>
                  </div>

                  {selectedDocuments?.length > 0 && (
                    <>
                      <Button
                        onClick={handleBulkArchive}
                        iconName="Archive"
                        variant="outline"
                        size="sm"
                      >
                        Archive ({selectedDocuments?.length})
                      </Button>
                      <Button
                        onClick={handleBulkDelete}
                        iconName="Trash2"
                        variant="destructive"
                        size="sm"
                      >
                        Delete ({selectedDocuments?.length})
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Filter Panel */}
            {showFilterPanel && (
              <DocumentFilterPanel
                filters={filters}
                setFilters={setFilters}
                employees={employees}
                onClose={() => setShowFilterPanel(false)}
              />
            )}

            {/* Document Library */}
            {filteredDocuments?.length === 0 ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No documents found</h3>
                <p className="text-gray-600 mb-6">
                  {searchTerm || selectedCategory !== 'all' ?'Try adjusting your filters or search term' :'Upload your first document to get started'}
                </p>
                {!searchTerm && selectedCategory === 'all' && (
                  <Button
                    onClick={() => setShowUploadModal(true)}
                    iconName="Upload"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Upload Document
                  </Button>
                )}
              </div>
            ) : (
              <div
                className={`${
                  viewMode === 'grid' ?'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6' :'space-y-4'
                }`}
              >
                {filteredDocuments?.map(document => (
                  <DocumentCard
                    key={document?.id}
                    document={document}
                    viewMode={viewMode}
                    isSelected={selectedDocuments?.includes(document?.id)}
                    onToggleSelect={() => toggleDocumentSelection(document?.id)}
                    onPreview={() => handlePreview(document)}
                    onDownload={() => handleDownload(document)}
                    onArchive={() => handleArchive(document?.id)}
                    onDelete={() => handleDelete(document?.id)}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modals */}
      {showUploadModal && (
        <DocumentUploadModal
          employees={employees}
          onClose={() => setShowUploadModal(false)}
          onSuccess={handleUploadSuccess}
        />
      )}

      {showPreviewModal && selectedDocument && (
        <DocumentPreviewModal
          document={selectedDocument}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedDocument(null);
          }}
          onDownload={() => handleDownload(selectedDocument)}
        />
      )}
    </div>
  );
};

export default DocumentManagementCenter;